import { computed, ref, watch } from 'vue'
import { consola } from 'consola'
import { useModalStore, modalStateEnum } from '@shared/model'
import { useAuthState } from '@entities/auth'
import { useUserProfileStore, toSelectedDeliveryAddress } from '@entities/user'
import type { UserAddress } from '~/utils/types/user'
import { useInstitutionStore, apiInstitutions, hasFreshBranchChoice } from '@entities/institution'
import { useCartStore } from '@entities/order'
import { useCheckoutFormStore } from '~/features/basket/@x/delivery-address-prompt'
import { isBranchPromptOpen } from '~/features/branch-select-prompt/@x/delivery-address-prompt'

const logger = consola.withTag('delivery-address-prompt')

/** Задержка показа промпта после открытия приложения (как в Dodo — даём осмотреться). */
const PROMPT_DELAY_MS = 30_000
/** Повторная попытка показа, если в нужный момент открыта другая модалка. */
const RETRY_DELAY_MS = 5000

// ── Состояние модуля (singleton на одну загрузку страницы) ──
// isOpen — открыта ли нижняя шторка. dismissedThisLoad — пользователь нажал «Не сейчас»:
// в эту загрузку больше не дёргаем (флаг намеренно НЕ персистентный — по требованию
// показываем при каждом новом открытии приложения).
const isOpen = ref(false)

/** Открыта ли адресная шторка — для координации со шторкой выбора филиала. */
export const isAddressPromptOpen = computed(() => isOpen.value)

let dismissedThisLoad = false
let timer: ReturnType<typeof setTimeout> | null = null
// Для какого пользователя логика уже отработала. Раньше это был голый boolean, и
// вход под другим аккаунтом в той же загрузке наследовал чужое состояние: run()
// молча выходил, адрес нового пользователя не применялся.
let handledForUserId: number | null = null
let watcherInstalled = false

function clearTimer(): void {
  if (timer != null) {
    clearTimeout(timer)
    timer = null
  }
}

/**
 * Стартовый промпт «Укажите адрес для заказа».
 *
 * Сценарий входа в приложение для авторизованного пользователя:
 *  1. Есть дефолтный адрес → молча применяем его (метод «доставка» + резолв филиала по
 *     координатам), меню сразу = филиал доставки. Промпт НЕ показываем — мы знаем, где он.
 *  2. Адрес доставки уже выбран ранее (восстановлен из хранилища) → не дёргаем.
 *  3. Иначе через {@link PROMPT_DELAY_MS} показываем нижнюю шторку с выбором адреса.
 *
 * Запуск через {@link start} вешает вотчер на авторизацию: логика срабатывает и когда
 * пользователь уже вошёл (immediate), и когда логинится по ходу сессии (через модалку).
 *
 * Филиал для доставки подбирает сервер по координатам и составу корзины (эксклюзивные
 * товары филиала не «перекидываются»), поэтому здесь резолв повторяет логику ModalMapYandex.
 */
export function useDeliveryAddressPrompt() {
  const modalStore = useModalStore()
  const { isLoggedIn } = useAuthState()

  /** Доставляемый ли адрес: активный, с координатами (без них филиал не подобрать). */
  function isDeliverable(a: UserAddress): boolean {
    return (
      a.isActive && Number.isFinite(Number(a.latitude)) && Number.isFinite(Number(a.longitude))
    )
  }

  /** Доставляемый ли дефолтный адрес: активный, помечен дефолтным, с координатами. */
  function findDefaultAddress(addresses: UserAddress[]): UserAddress | null {
    return addresses.find((a) => a.isDefault && isDeliverable(a)) ?? null
  }

  const normalizeAddress = (value: string) => value.trim().replaceAll(/\s+/g, ' ').toLowerCase()

  /** Тот же адрес в списке — по точному совпадению строки (регистр и пробелы не в счёт). */
  function findByAddressString(addresses: UserAddress[], address: string): UserAddress | null {
    const wanted = normalizeAddress(address ?? '')
    if (!wanted) return null
    return addresses.find((a) => isDeliverable(a) && normalizeAddress(a.address) === wanted) ?? null
  }

  /**
   * Применяет адрес как адрес доставки: ставит метод «доставка», адрес/зону в форму
   * заказа и подбирает филиал доставки по координатам и текущему составу корзины.
   *
   * @param overwriteAddress перезаписывать ли адрес/зону/метод получения. false —
   * когда адрес уже выбран пользователем в этой сессии: его выбор не трогаем, но
   * координаты и подбор филиала обновляем в любом случае (без координат заказ на
   * доставку не оформить).
   */
  async function applyAddress(
    addr: UserAddress,
    { overwriteAddress = true }: { overwriteAddress?: boolean } = {},
  ): Promise<void> {
    const checkoutStore = useCheckoutFormStore()
    const profile = useUserProfileStore()
    const institutionStore = useInstitutionStore()
    const cartStore = useCartStore()

    // Единый формат: строка точки и уточнения (подъезд, квартира) — как во всех
    // остальных местах выбора адреса.
    const selected = toSelectedDeliveryAddress(addr)
    const zona = selected.zona == null ? null : String(selected.zona)

    if (overwriteAddress) {
      checkoutStore.setField('method_receipt', 'delivery')
      checkoutStore.setField('delivery_address', { address: selected.address, zona: zona ?? undefined })
      checkoutStore.setField('delivery_zone_id', zona)
      await profile.setSelectedDeliveryAddress(selected)
    }

    const lat = Number(addr.latitude)
    const lng = Number(addr.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

    // Координаты — источник истины для авторитетного подбора филиала на бэке.
    checkoutStore.setDeliveryCoords({ latitude: lat, longitude: lng })

    // Пользователь недавно (меньше суток назад) ЯВНО выбрал филиал — стартовый
    // авто-роутинг по дефолтному адресу его не перетирает: именно из-за этого
    // «слетала локация» после F5. Координаты выше уже сохранены, так что при
    // оформлении доставки сервер всё равно авторитетно подберёт филиал.
    if (hasFreshBranchChoice()) {
      logger.info('Свежий выбор филиала пользователем — авто-роутинг филиал не меняет')
      return
    }

    // institutionStore.currentId — мёртвый ref (всегда 1); берём id реально
    // загруженного заведения, как в ModalMapYandex.
    const instId =
      (institutionStore.institution as { id?: number } | null)?.id ||
      Number(useRuntimeConfig().public.institutionID)
    if (!instId) return

    // Состав корзины: сервер подберёт только тот филиал, который делает ВЕСЬ заказ.
    const productIds = cartStore.cart
      .filter((i: any) => i.itemType !== 'combo')
      .map((i: any) => String(i.productId ?? i.id))
    const comboIds = cartStore.cart
      .filter((i: any) => i.itemType === 'combo')
      .map((i: any) => String(i.comboId))
      .filter((id: string) => id && id !== 'undefined')

    try {
      const { data } = await apiInstitutions.resolveDeliveryBranch(instId, {
        latitude: lat,
        longitude: lng,
        orderSum: cartStore.totalPrice,
        productIds,
        comboIds,
      })
      // На старте корзина обычно пуста → outOfZone/cartUnavailable маловероятны. Если
      // всё же не подобрался филиал — не переключаемся (оставляем дефолтный из шапки),
      // пользователь увидит ошибку уже при попытке оформить.
      if (data?.branchId && !data.outOfZone && !data.cartUnavailable) {
        institutionStore.setSelectedBranchId(Number(data.branchId))
        if (data.zoneId) checkoutStore.setField('delivery_zone_id', data.zoneId)
        logger.info('Применён дефолтный адрес, филиал доставки подобран', {
          branchId: data.branchId,
          zoneId: data.zoneId,
          distanceKm: data.distanceKm,
        })
      } else {
        logger.warn('Дефолтный адрес: филиал не подобран', {
          outOfZone: data?.outOfZone,
          cartUnavailable: data?.cartUnavailable,
        })
      }
    } catch (error) {
      // Сбой резолва не критичен: координаты сохранены, бэк подберёт филиал при оформлении.
      logger.error('Не удалось подобрать филиал для дефолтного адреса', error)
    }
  }

  /** Открыть нижнюю шторку, если условия всё ещё выполняются. */
  function fire(): void {
    timer = null
    if (!import.meta.client) return
    if (!isLoggedIn.value) return
    if (dismissedThisLoad) return
    // Уже есть выбранный адрес доставки — не нудим.
    if (useUserProfileStore().getSelectedDeliveryAddress != null) return
    // Поверх другой открытой модалки (логин/онбординг/карта) или шторки выбора
    // филиала не показываем — подождём и повторим попытку, когда она закроется.
    if (modalStore.isOpen || isBranchPromptOpen.value) {
      timer = setTimeout(fire, RETRY_DELAY_MS)
      return
    }
    isOpen.value = true
    logger.info('Показываем стартовый промпт выбора адреса')
  }

  /**
   * Ядро: решает — применить дефолтный адрес, промолчать или запланировать промпт.
   * Вызывается вотчером при входе пользователя (см. {@link start}).
   */
  async function run(): Promise<void> {
    if (!import.meta.client) return
    if (!isLoggedIn.value) return

    const profile = useUserProfileStore()
    const currentUserId = profile.getUser?.id ?? null
    if (handledForUserId !== null && handledForUserId === currentUserId) return
    handledForUserId = currentUserId

    // Список адресов подтягивается при логине; подстрахуемся разовым фолбэком.
    if (profile.getUserAddresses.length === 0) {
      await profile.fetchUserAddresses()
    }

    // Что применяем: уже выбранный адрес приоритетнее дефолтного — его выбрал
    // пользователь. Дефолт берём, только когда выбора ещё нет.
    const selected = profile.getSelectedDeliveryAddress
    const target = selected
      ? findByAddressString(profile.getUserAddresses, selected.address)
      : findDefaultAddress(profile.getUserAddresses)

    if (target) {
      // Знаем, куда доставлять.
      //
      // Координаты проставляем ВСЕГДА: без них бэкенд не даст оформить доставку, а
      // прийти им больше неоткуда, если пользователь не пойдёт на карту. Раньше весь
      // applyAddress стоял под условием «адрес ещё не выбран», и гонка (восстановление
      // адреса из хранилища через 2 с обгоняло сетевой fetchUserAddresses) оставляла
      // заказ без координат: адрес в форме есть, а оформить нельзя.
      //
      // Адрес и зону при этом перетираем только когда выбора не было — иначе затрём
      // осознанный выбор пользователя в этой сессии.
      await applyAddress(target, { overwriteAddress: selected == null })
      return
    }

    // Адрес выбран, но в списке его нет (например, разовый ввод на карте) — трогать
    // нечего, координаты у него уже свои.
    if (selected != null) return
    if (dismissedThisLoad) return
    clearTimer()
    timer = setTimeout(fire, PROMPT_DELAY_MS)
  }

  /**
   * Точка входа: вызывается из layout в setup. Вешает вотчер на авторизацию, чтобы
   * логика отработала и для уже вошедшего пользователя, и при логине по ходу сессии.
   */
  function start(): void {
    if (!import.meta.client) return
    if (watcherInstalled) return
    watcherInstalled = true
    watch(
      isLoggedIn,
      (loggedIn) => {
        if (loggedIn) void run()
      },
      { immediate: true },
    )
  }

  /** «Указать» — закрываем шторку и открываем карту выбора адреса (режим доставки). */
  function chooseAddress(): void {
    isOpen.value = false
    clearTimer()
    modalStore.open(modalStateEnum.ModalMapYandex, { orderType: 'delivery' })
  }

  /** «Не сейчас» — прячем шторку до следующего открытия приложения. */
  function dismiss(): void {
    isOpen.value = false
    dismissedThisLoad = true
    clearTimer()
  }

  return { isOpen, start, chooseAddress, dismiss }
}
